// ItemTags — Sprint 19BH · v4 anchor-first ingest pillar A (universal tags).
//
// Why this exists (v4-rewrite.md §2.1):
//   Every LLM-generated item (mcq · true_false · flashcard · essay ·
//   free_response · proof_drill · glossary · lecture_note · mindmap ·
//   reading_passage) MUST extend this schema. The three id arrays connect
//   items to the frozen CurriculumOutline (pillar B) anchor.
//
// Why arrays (not singular as in v3):
//   K10 — v3 used singular `caseId: string`. 19BG+3 Phase B observed that
//   subjective items (essay/freeResp/proofDrill) often span multiple chapters,
//   and the v3 workaround (parallel `caseIds: string[]` field alongside the
//   singular) caused F37 schema-floor regressions. v4 makes arrays the only
//   form — singular is the special case `caseIds.length === 1`. Cohesion no
//   longer comes from "same LLM call"; it comes from "same anchor tag set".
//
// Empty arrays are valid (and meaningful):
//   - `topicIds: []`     — item is not specifically attached to a topic
//   - `caseIds: []`      — item is course-wide (rare; not currently used)
//   - `conceptIds: []`   — item is generic / framework-level (rare)
//   Audit rule `min_per_concept` (Day 3) distinguishes "intentional empty"
//   from "missing tag" via the per-stage MANIFEST.outlineLevel config.
//
// Tag enforcement is two-layer:
//   1. Zod superRefine (Step 1.8 `enforceItemTagsAgainstOutline`) — strict at
//      generation time. LLM output with a tag NOT in the outline is rejected
//      and routed to retry-with-hint (memory feedback_generation_quality).
//   2. Audit `tag_in_outline` rule (Day 3) — recomputes post-hoc; flags
//      items where Phase 1 was bypassed (e.g. user-edited package).
import { z } from 'zod';
import { OUTLINE_ID_PATTERN, OUTLINE_ID_MESSAGE } from './curriculum-outline.js';
/**
 * Normalize an LLM-emitted slug-candidate string into the canonical OUTLINE_ID_PATTERN
 * shape (`/^[a-z][a-z0-9_]{0,63}$/`). Returns `null` when the value is unrecoverable
 * (e.g. only Chinese chars, or empty after stripping).
 *
 * 19BH+2 Day 1 Round 3 (DEBT-19BH+1-PHASE3-BUNDLE-LATENCY-USAGE). cs3052 Run 3 raw
 * output captured (commit 05a7853 · runId a5358ac4) revealed LLM emitting:
 *   - TitleCase: "NP-Completeness", "Cook-Levin Theorem"
 *   - Bilingual:  "turing_machine (图灵机)"
 * even after LANGUAGE_MODE_RULES added the ID-exception clause. Pure-prompt
 * approach hit ceiling after 3 rounds (Round 1 prompt cap · Round 2 bilingual
 * exception · Round 3 +1 retry).
 *
 * Per LLM-native architecture (§14.3 post-LLM normalization), technical-shape
 * coercion is the correct layer. NOT loosening the schema floor (schema still
 * rejects unrecoverable entries); collapsing recoverable-but-malformed values
 * to canonical form. Memory feedback_generation_quality守则 satisfied: we did
 * prompt+retry-hint+raw-output-log (Rounds 1-3) before falling to engineering.
 *
 * Transform pipeline:
 *   1. strip parentheticals `(any content)` — removes bilingual glosses
 *   2. lowercase
 *   3. replace whitespace + hyphens + dots → `_`
 *   4. strip leading/trailing underscores
 *   5. drop remaining non-`[a-z0-9_]` chars (e.g. apostrophes, unicode)
 *   6. collapse repeated underscores
 *   7. truncate to 64 chars
 *   8. ensure first char is a-z (drop if leading digit/underscore can't be fixed)
 *
 * Examples:
 *   "NP-Completeness"           → "np_completeness"
 *   "Cook-Levin Theorem"        → "cook_levin_theorem"
 *   "turing_machine (图灵机)"   → "turing_machine"
 *   "Savitch's Theorem"         → "savitchs_theorem"
 *   "P"                         → "p"
 *   "图灵机"                    → null (no recoverable ASCII)
 *   ""                          → null
 */
export function normalizeSlug(raw) {
    if (typeof raw !== 'string')
        return null;
    let s = raw;
    // 1. strip parentheticals (incl. nested-flat, e.g. "name (gloss)")
    s = s.replace(/\([^)]*\)/g, '');
    // 2. lowercase
    s = s.toLowerCase();
    // 3. whitespace + hyphen + dot → underscore
    s = s.replace(/[\s\-.]+/g, '_');
    // 4. drop non-[a-z0-9_] (unicode, apostrophes, etc.)
    s = s.replace(/[^a-z0-9_]/g, '');
    // 5. collapse repeated underscores
    s = s.replace(/_+/g, '_');
    // 6. strip leading/trailing underscores
    s = s.replace(/^_+|_+$/g, '');
    // 7. truncate to 64 chars (then re-strip trailing underscore if cut mid-word)
    if (s.length > 64)
        s = s.slice(0, 64).replace(/_+$/, '');
    // 8. must start with a-z
    if (s.length === 0 || !/^[a-z]/.test(s))
        return null;
    return s;
}
/**
 * 19BH+2 Day 1 Round 3 · normalize an id-array of slug candidates. Keeps entries
 * that already satisfy OUTLINE_ID_PATTERN unchanged; coerces near-misses via
 * normalizeSlug; silently drops unrecoverable entries. Dedupes preserving order.
 *
 * Why drop unrecoverable rather than throw: schema retry-with-hint already
 * surfaces violations; this preprocess is a best-effort cleanup that lets the
 * other 90% of the bundle land. Drop count is invisible to LLM but visible in
 * the audit `tag_in_outline` rule (Day 3) which compares against outline.
 */
function normalizeIdArray(arr) {
    const out = [];
    const seen = new Set();
    for (const entry of arr) {
        if (typeof entry !== 'string')
            continue;
        let candidate;
        if (OUTLINE_ID_PATTERN.test(entry)) {
            candidate = entry;
        }
        else {
            candidate = normalizeSlug(entry);
        }
        if (candidate && !seen.has(candidate)) {
            seen.add(candidate);
            out.push(candidate);
        }
    }
    return out;
}
/**
 * Universal item tags shared by every LLM-generated item. Item schemas extend
 * this via `ItemTagsSchema.extend({ ... })` (Day 2 Step 2.4).
 *
 * All three fields are required arrays (may be empty). Each entry must match
 * the canonical outline id pattern (snake_case ASCII, 1–64 chars). Pattern
 * enforcement here is a redundant guard — the primary check is that each id
 * is present in the bound CurriculumOutline (Step 1.8 superRefine).
 */
/**
 * v3 → v4 item-tags compatibility preprocess (19BH Day 2 Step 2.4).
 *
 * Item schemas wrap with `z.preprocess(coerceItemTagsV3ToV4, ...)` so legacy
 * IDB data (singular `caseId`/`topicId`/`conceptTag`) and v4 data (plural
 * `caseIds`/`topicIds`/`conceptIds`) both parse without rejection.
 *
 * Lifecycle:
 *   - v3 LLM output:     `{ caseId: 'foo', conceptTag: 'bar' }`
 *     → coerce → `{ caseIds: ['foo'], conceptIds: ['bar'], caseId: ..., conceptTag: ... }`
 *   - v4 LLM output:     `{ caseIds: ['foo'], conceptIds: ['bar'] }` → unchanged
 *   - Day 5 stored data: written with BOTH singular (alias for engine) AND
 *     plural (canonical) → both fields present, schema accepts.
 *   - 19BI cleanup: singular fields drop from schemas + storage migration removes them.
 *
 * The function is intentionally permissive — never overwrites an explicitly
 * present `caseIds` array. Non-string singular values (null, missing) yield
 * empty arrays so v4 audit `tag_in_outline` rule flags them as untagged.
 */
export function coerceItemTagsV3ToV4(obj) {
    if (typeof obj !== 'object' || obj === null)
        return obj;
    const item = obj;
    const caseIds = item['caseIds'];
    const caseId = item['caseId'];
    const topicIds = item['topicIds'];
    const topicId = item['topicId'];
    const conceptIds = item['conceptIds'];
    const conceptTag = item['conceptTag'];
    // 19BH+2 Day 1 Round 3 · normalizeIdArray applied after v3→v4 array
    // collection. Coerces LLM-emitted TitleCase / hyphenated / bilingual slug
    // candidates to canonical snake_case ASCII. Strict-pattern entries pass
    // through unchanged; unrecoverable entries (e.g. Chinese-only) drop silently.
    // See normalizeSlug() doc for transform pipeline + cs3052 Run 3 evidence.
    const rawCaseIds = Array.isArray(caseIds)
        ? caseIds
        : typeof caseId === 'string'
            ? [caseId]
            : [];
    const rawTopicIds = Array.isArray(topicIds)
        ? topicIds
        : typeof topicId === 'string'
            ? [topicId]
            : [];
    const rawConceptIds = Array.isArray(conceptIds)
        ? conceptIds
        : typeof conceptTag === 'string'
            ? [conceptTag]
            : [];
    const finalCaseIds = normalizeIdArray(rawCaseIds);
    const finalTopicIds = normalizeIdArray(rawTopicIds);
    const finalConceptIds = normalizeIdArray(rawConceptIds);
    return {
        ...item,
        caseIds: finalCaseIds,
        topicIds: finalTopicIds,
        conceptIds: finalConceptIds,
        // 19BI · backfill singular aliases so all existing frontend code that reads
        // item.caseId / item.topicId (stats.ts, item-filter.ts, every view) works
        // without changes. LLM output is inconsistent — sometimes emits singular,
        // sometimes only plural. This normalizes both paths.
        caseId: item['caseId'] ?? finalCaseIds[0] ?? null,
        topicId: item['topicId'] ?? finalTopicIds[0] ?? null,
        conceptTag: item['conceptTag'] ?? finalConceptIds[0] ?? null,
    };
}
export const ItemTagsSchema = z.object({
    /**
     * Chapter ids this item belongs to. Multiple entries iff the item is a
     * cross-chapter subjective (essay / freeResp / proofDrill). Each id MUST
     * be a member of `CurriculumOutline.chapters[].id`.
     */
    caseIds: z.array(z.string().regex(OUTLINE_ID_PATTERN, OUTLINE_ID_MESSAGE))
        .describe('Chapter ids this item belongs to. Multiple entries for cross-chapter subjective items.'),
    /**
     * Topic ids this item attaches to. Empty = item is chapter-level without
     * specific topic. Each id MUST belong to one of the chapters in caseIds.
     */
    topicIds: z.array(z.string().regex(OUTLINE_ID_PATTERN, OUTLINE_ID_MESSAGE))
        .describe('Topic ids this item attaches to. Empty allowed (chapter-level item without specific topic).'),
    /**
     * Concept ids this item exercises. A single item commonly cites 2–4
     * concepts; mocks may cite more. Each id MUST belong to one of the
     * chapters in caseIds.
     */
    conceptIds: z.array(z.string().regex(OUTLINE_ID_PATTERN, OUTLINE_ID_MESSAGE))
        .describe('Concept ids this item exercises. Drives cross-stage dedup and regen context (manifest.regenContext.needsSameStageInChapter).'),
}).passthrough();
//# sourceMappingURL=item-tags.js.map